// Audition voices in place using existing recordings of the same text.
import { ENGLISH_KNOWN_DEFAULTS, type KnownVoiceCatalogue } from '../../../core/src/audio/knownVoiceChoice'
import type { CourseClipMenu } from '../../../core/src/audio/clipMenu'

export type AuditionKind = 'intro' | 'longest-clip'
export interface AuditionClip { id: string; kind: AuditionKind }

/** Pick once by text coverage, never independently by voice. Narration keys are
 * presentation/LEGO identities, so they cannot guarantee identical spoken text. */
function sharedAudition(keys: Iterable<string>, voices: string[], voice: string, clipId: (key: string, voice: string) => string | undefined): AuditionClip | null {
  const required = [...new Set(voices.filter(Boolean))]
  if (!voice || !required.includes(voice)) return null
  // Only the winner is needed; sorting the full course repeats work for each
  // voice row rendered by the picker. Keep the same longest/text tie-break.
  let key: string | undefined
  for (const candidate of keys) {
    // Coverage cannot make a shorter/later text beat an eligible winner.
    if (key !== undefined && (candidate.length < key.length ||
      (candidate.length === key.length && candidate.localeCompare(key) >= 0))) continue
    if (!required.every(id => clipId(candidate, id))) continue
    key = candidate
  }
  return key ? { id: clipId(key, voice)!, kind: 'longest-clip' } : null
}

/** All rows, including Default, compare recordings of one shared prompt. */
export function knownAudition(catalogue: KnownVoiceCatalogue | undefined, voice: string, voices?: string[]): AuditionClip | null {
  if (!catalogue) return null
  const menuLines = catalogue.menu?.known.lines
  const keys = new Set([...Object.keys(catalogue.clips), ...Object.keys(menuLines || {})])
  // Read only the candidates that can win. Menu entries override legacy ids,
  // including empty ids; absent menu voices retain their legacy coverage.
  return sharedAudition(keys, voices ?? catalogue.voices.map(v => v.id), voice, (key, id) => {
    const clips = menuLines?.[key]?.clips
    return clips && Object.prototype.hasOwnProperty.call(clips, id)
      ? clips[id].id
      : catalogue.clips[key]?.[id]
  })
}

/** Both model pickers share a text across the entire offerable cast. */
export function modelAudition(menu: CourseClipMenu | undefined, voice: string): AuditionClip | null {
  if (!menu) return null
  const voices = [...menu.target.voices.filter(v => v.offerable).map(v => v.id), ...Object.values(menu.target.cast)]
  // Read the menu directly: each picker row needs one winner, not a copied
  // record of every clip id in the course.
  return sharedAudition(Object.keys(menu.target.lines), voices, voice, (key, id) => menu.target.lines[key].clips[id]?.id)
}

export const englishKnownLanguage = (language: string) => ['eng', 'en'].includes(language.toLowerCase().split(/[-_]/)[0])

// One shared element: reusing it keeps the mobile gesture unlock, and starting one voice stops the last.
let element: HTMLAudioElement | null = null
let playing: string | null = null
const listeners = new Set<(id: string | null) => void>()
const set = (id: string | null) => { playing = id; listeners.forEach(l => l(id)) }
export const onAuditionChange = (l: (id: string | null) => void) => { listeners.add(l); return () => listeners.delete(l) }
export function stopAudition() { element?.pause(); set(null) }
export async function toggleAudition(key: string, clip: AuditionClip): Promise<void> {
  if (playing === key) return stopAudition()
  element ??= new Audio()
  element.onended = () => set(null)
  element.onerror = () => set(null)
  element.src = `/api/audio/${clip.id}`
  set(key)
  try { await element.play() } catch { set(null) }
}

/** Who "Default" is for the known side, named so the learner can hear and see it. Mirrors the
 * rule chooseKnownVoiceUrl applies; it decides nothing itself. */
export function knownDefault(catalogue: KnownVoiceCatalogue | undefined, language?: string): { id: string; name: string } | null {
  if (!catalogue) return null
  const named = [...(catalogue.menu?.known.voices || []), ...catalogue.voices]
  const english = ['eng', 'en'].includes(language || '') ? ENGLISH_KNOWN_DEFAULTS : []
  for (const id of [...english, catalogue.menu?.known.cast.teacher || '']) {
    const voice = id && named.find(v => v.id === id)
    if (voice) return { id: voice.id, name: voice.name }
  }
  return null
}
